import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import {
  LocalizationProvider,
  loadMessages,
  IntlProvider,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import esMessages from '../../utils/gridMessageKey.json';
import { ApplicationState } from '../../store';
import {
  NodeUserAccessControl,
  UploadedAccessControlInfo,
} from '../../Interfaces/AccessControl';
import {
  Grid,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar,
} from '@progress/kendo-react-grid';
import StringResourceManager, { GetString } from '../StringResourceManager';
import { createStyles, Divider, makeStyles, Theme } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import { DataResult, process, State } from '@progress/kendo-data-query';
import {
  setExpandedState,
  setGroupIds,
} from '@progress/kendo-react-data-tools';
import { InputPrefix, TextBox } from '@progress/kendo-react-inputs';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import LaneSelectionDialog from './LaneSelectionDialog';
import {
  Upload,
  UploadOnStatusChangeEvent,
  UploadFileStatus,
  UploadFileInfo,
} from '@progress/kendo-react-upload';
import Papa from 'papaparse';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import {
  fetchLatestUploadAccessControlCSV,
  fetchUploadedAccessControlCSV,
  savingAccessControlGridState,
} from '../../store/AccessControl/AccessControlActions';
import { initialDataState } from '../../store/AccessControl/AccessControlState';

loadMessages(esMessages, 'fr-CA');

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    error: {
      fontSize: '1.5rem',
    },
    toolbarButton: {
      height: 40,
      minWidth: 160,
      marginRight: 5,
      marginTop: 10,
    },
    uploadButton: {
      minWidth: 160,
      marginRight: 5,
    },
    toolbarButtonContainer: {
      display: 'grid',
    },
    toolbarButtonGrid: {
      display: 'flex',
      padding: '5px',
    },
    column: {
      textALign: 'center',
    },
    icon: {
      marginTop: '-5px',
      marginLeft: '5px',
    },
    send: {
      fontSize: '1.5rem',
    },
    fontSize: {
      fontSize: '0.9rem',
    },
    span: {
      fontWeight: 'bold',
      fontSize: '1rem',
      margin: '10px',
    },
  })
);

const AccessControlContainer: React.FC = (): React.ReactElement => {
  const classes = useStyles();

  const dispatch: Dispatch<any> = useDispatch();

  const initialDataStates: State = useSelector(
    (state: ApplicationState) =>
      state.accessControlReducer.accessControlGridState
  );

  const uploadedAccessControl: UploadedAccessControlInfo = useSelector(
    (state: ApplicationState) =>
      state.accessControlReducer.uploadedAccessControlInfo
  );

  const uploadAccessControlFilePath: string = useSelector(
    (state: ApplicationState) => state.accessControlReducer.filePath
  );

  const latestAccessControlCSVFileName: string | undefined = useSelector(
    (state: ApplicationState) => state.accessControlReducer.fileName
  );

  const latestAccessControlCSV: NodeUserAccessControl[] | undefined =
    useSelector(
      (state: ApplicationState) =>
        state.accessControlReducer.latestAccessControlCSV
    );

  const [dataState, setDataState] = React.useState<State>(initialDataStates);

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const [collapsedState, setCollapsedState] = React.useState<string[]>([]);

  const [commandSend, displayCommandSend] = React.useState(false);

  const [laneSelectionDialogModalVisible, setLaneSelectionDialogModalVisible] =
    React.useState<boolean>(false);

  const [fileName, setFileName] = React.useState<any>(
    uploadedAccessControl?.fileInfo[0]
      ? uploadedAccessControl.fileInfo[0].name
      : ''
  );

  const [messageResponse, setMessageResponse] = React.useState<string>('');

  const [notificationStyle, setNotificationStyle] = React.useState<any>();

  const [accessControlData, setAccessControlData] = React.useState<
    NodeUserAccessControl[]
  >([]);

  const [showImportStatus, setShowImportStatus] =
    React.useState<boolean>(false);

  const processWithGroups = (data: any, dataState: State): DataResult => {
    if (dataState === undefined) {
      dataState = initialDataState;
    }

    const newDataState = process(data, dataState);

    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const [resultState, setResultState] = React.useState<DataResult>(
    processWithGroups(accessControlData, dataState)
  );

  const newData = setExpandedState({
    data: resultState.data,
    collapsedIds: collapsedState,
  });

  function convertToArray(input: string, separator = ';') {
    return input
      .split(separator)
      .map((item: any) => item.trim())
      .filter((item: any) => item !== '');
  }

  const handleFileUpload = (
    data: any,
    fileUploadInfo: UploadFileInfo[],
    message: string
  ) => {
    let accessMenu: string[] = [];

    data.shift();
    const newCSVImportedData = data.filter((data: any) => data.length > 1);
    const importUsersAccessControlData: NodeUserAccessControl[] =
      newCSVImportedData.map((column: any) => {
        return {
          UserID: column[0],
          UserName: column[1],
          Password: column[2],
          Barcode: column[3],
          AccessRights: convertToArray(column[4]),
          StoreNumbers: column[5],
          BarcodeExpiry: column[6],
          PasswordExpiry: column[7],
        };
      });

    importUsersAccessControlData.forEach(user => {
      if (Array.isArray(user?.AccessRights)) {
        user.AccessRights.forEach(right => {
          if (!accessMenu.includes(right)) {
            accessMenu.push(right);
          }
        });
      }
    });

    const uploadInfo: UploadedAccessControlInfo = {
      fileInfo: fileUploadInfo,
      UsersAccessControl: importUsersAccessControlData,
    };

    fetchLatestUploadAccessControlCSV(dispatch);

    setFileName(uploadInfo.fileInfo[0].name);

    fetchUploadedAccessControlCSV(dispatch, uploadInfo);

    setNotificationStyle('success');
    setShowImportStatus(true);
    setMessageResponse(message);
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(
      latestAccessControlCSV,
      event.dataState
    );

    savingAccessControlGridState(dispatch, event.dataState);
    setResultState(newDataState);
  };

  React.useEffect(() => {
    let timeoutId: any;
    if (showImportStatus) {
      timeoutId = setTimeout(() => {
        setShowImportStatus(false);
        setMessageResponse('');
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [messageResponse]);

  const saveUrl = `AccessControl/UploadAccessControlConfig`;

  const onStatusChange = (event: UploadOnStatusChangeEvent) => {
    if (event?.response?.status === 200) {
      event.newState.forEach(file => {
        if (file.status === UploadFileStatus.Uploaded) {
          const rawFile = file.getRawFile ? file.getRawFile() : undefined;
          if (rawFile) {
            Papa.parse(rawFile, {
              complete: (result: any) => {
                handleFileUpload(
                  result.data,
                  event.affectedFiles,
                  event.response?.response
                );
              },
            });
          }
        }
      });
    } else {
      setNotificationStyle('error');
      setShowImportStatus(true);
      setMessageResponse(event.response?.response.data);
    }
  };

  React.useEffect(() => {
    let timeoutId: any;
    if (commandSend) {
      timeoutId = setTimeout(() => {
        displayCommandSend(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [commandSend]);

  React.useEffect(() => {
    if (initialDataStates) {
      setDataState(initialDataStates);
    }
  }, [initialDataStates]);

  const setDisplayCommandSent = (bool: boolean) => {
    displayCommandSend(bool);
  };

  React.useEffect(() => {
    fetchLatestUploadAccessControlCSV(dispatch);
  }, []);

  React.useEffect(() => {
    setFileName(latestAccessControlCSVFileName);
    setResultState(processWithGroups(latestAccessControlCSV ?? [], dataState));
  }, [latestAccessControlCSV]);

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <Grid
            style={{ height: '90vh' }}
            resizable={true}
            sortable={true}
            filterable={true}
            pageable={{
              buttonCount: 4,
              pageSizes: [20, 50, 100],
            }}
            data={resultState}
            {...dataState}
            fixedScroll={false}
            onDataStateChange={dataStateChange}
            total={latestAccessControlCSV?.length ?? 0}
          >
            <GridToolbar>
              <div className={classes.toolbarButtonContainer}>
                <div className={classes.toolbarButtonGrid}>
                  <StringResourceManager
                    variant="body1"
                    style="importCSVlabel"
                    stringKey={'IMPORT_CSV_LABEL'}
                    gutterBottom={true}
                  />
                  <Upload
                    className={classes.uploadButton}
                    batch={false}
                    restrictions={{
                      allowedExtensions: ['.csv'],
                    }}
                    defaultFiles={[]}
                    saveUrl={saveUrl}
                    autoUpload={true}
                    multiple={false}
                    onStatusChange={onStatusChange}
                  />
                  <Button
                    type="submit"
                    className={classes.toolbarButton}
                    onClick={() => setLaneSelectionDialogModalVisible(true)}
                    disabled={fileName ? false : true}
                  >
                    {GetString('SEND_CSV_TO_LANE')}
                  </Button>
                </div>
                <div className={classes.toolbarButtonGrid}>
                  <TextBox
                    value={latestAccessControlCSVFileName}
                    readOnly={true}
                    prefix={() => (
                      <>
                        <InputPrefix style={{ backgroundColor: '#EBEBE4' }}>
                          <span className={classes.span}>Filename</span>
                        </InputPrefix>
                      </>
                    )}
                    style={{ width: 323, height: 38 }}
                  />
                </div>
                <Divider />
              </div>
            </GridToolbar>
            <GridColumn
              field="UserID"
              title={GetString('ID')}
              width="150px"
              filterable={true}
            />
            <GridColumn
              field="UserName"
              title={GetString('USERNAME_PLACEHOLDER')}
              width="220px"
              filterable={true}
            />
            <GridColumn
              field="Password"
              title={GetString('PASSWORD_PLACEHOLDER')}
              width="220px"
              filterable={false}
            />
            <GridColumn
              field="Barcode"
              title={GetString('BARCODE_TITLE')}
              width="300px"
              filterable={false}
            />
            <GridColumn
              field="AccessRights"
              title={GetString('ACCESS_RIGHTS')}
              width="350px"
              filterable={false}
            />
            <GridColumn
              field="StoreNumbers"
              title={GetString('STORE_NUMBERS')}
              width="150px"
              filterable={true}
            />
            <GridColumn
              field="BarcodeExpiry"
              title={GetString('BARCODE_EXPIRY')}
              width="150px"
              filterable={false}
            />
            <GridColumn
              field="PasswordExpiry"
              title={GetString('PASSWORD_EXPIRY')}
              width="150px"
              filterable={false}
            />
          </Grid>
          {laneSelectionDialogModalVisible && (
            <LaneSelectionDialog
              handleCloseDialog={() =>
                setLaneSelectionDialogModalVisible(false)
              }
              setDisplayCommandSend={setDisplayCommandSent}
              fileName={fileName}
            />
          )}

          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {showImportStatus && (
                <Notification
                  className={classes.send}
                  type={{
                    style: notificationStyle,
                    icon: true,
                  }}
                  closable={true}
                >
                  {messageResponse}
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {commandSend && (
                <Notification
                  className={classes.send}
                  type={{
                    style:
                      uploadAccessControlFilePath !== '' ? 'success' : 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {
                    <StringResourceManager
                      stringKey={
                        uploadAccessControlFilePath !== ''
                          ? 'COMMAND_SENT_SUCCESSFULLY'
                          : 'COMMAND_SEND_FAILED'
                      }
                    />
                  }
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default AccessControlContainer;
